/**
 * Dashboard: "where am I right now?"
 *
 * M6 update: removed the live/manual price toggle. Price always comes from
 * the most recent successful CoinGecko fetch (persisted to LocalStorage so
 * page reloads have an immediate value to show before the next fetch resolves).
 *
 * Patch N changes:
 *   1. Live Data card moved to be the LAST section on the page (after
 *      EtfHoldings and NetWorth) and renamed: button now reads "Refresh
 *      prices" and triggers BOTH the BTC live fetch AND a refresh of every
 *      ETF holding's price in parallel via Promise.allSettled, instead of
 *      only refreshing BTC. The per-holding refresh buttons inside the
 *      EtfHoldings cards still work for fine-grained control.
 *   2. Goals card now has a small editor at the bottom: inline edit for
 *      label and BTC target on each existing goal, delete button per row,
 *      and an "add goal" form. Backed by new addGoal / updateGoal /
 *      removeGoal store actions added in this same patch.
 */

import { useMemo, useState, lazy, Suspense } from 'react';
import { useAppStore } from '../state/store.js';
import { selectDashboard } from '../state/selectors.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatBtc, formatSats, formatUsd, formatPct, formatDate, Delta, Pct } from '../components/Money.js';
import { StatBox } from '../components/StatBox.js';
import { CoinGeckoProvider } from '../io/live/coingecko.js';
import { fetchEtfPrice } from '../io/live/etf-price.js';
import { EtfHoldings } from '../components/EtfHoldings.js';
import { NetWorth } from '../components/NetWorth.js';
import { DecimalInput } from '../components/DecimalInput.js';
import { useAutoRefresh } from '../hooks/useAutoRefresh.js';
import { computeLoanState } from '../engine/loan-state.js';
import { healthFactor as hfFn, ltv as ltvFn } from '../engine/loop-loan.js';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

// Lazy-load the chart so Recharts stays out of the Dashboard's initial bundle.
const BtcPriceChart = lazy(() =>
  import('../components/BtcPriceChart.js').then(m => ({ default: m.BtcPriceChart })),
);

function ChartFallback(): JSX.Element {
  return (
    <section className="section">
      <p className="muted mono" style={{ fontSize: 'var(--fs-small)' }}>loading chart...</p>
    </section>
  );
}

const provider = new CoinGeckoProvider();

export function Dashboard(): JSX.Element {
  const purchases = useAppStore(s => s.purchases);
  const goals = useAppStore(s => s.goals);
  const liveSnapshot = useAppStore(s => s.liveSnapshot);
  const liveStatus = useAppStore(s => s.liveStatus);
  const liveError = useAppStore(s => s.liveError);
  const paceMethod = useAppStore(s => s.paceMethod);
  const setPaceMethod = useAppStore(s => s.setPaceMethod);
  const setLiveSnapshot = useAppStore(s => s.setLiveSnapshot);
  const setLiveStatus = useAppStore(s => s.setLiveStatus);

  // ETF refresh dependencies for the unified Refresh prices button.
  const etfHoldings = useAppStore(s => s.etfHoldings);
  // Patch DX: switched from updateEtfHolding (save-firing) to
  // setEtfLivePrice (no-save) so the 2-minute auto-refresh interval
  // stops firing cloud writes for transient price data. Same fix as DU
  // applied to BTC's setLiveSnapshot, extended here to ETF prices.
  const setEtfLivePrice = useAppStore(s => s.setEtfLivePrice);
  const setEtfPriceStatus = useAppStore(s => s.setEtfPriceStatus);
  const etfPriceStatus = useAppStore(s => s.etfPriceStatus);

  // Patch N: goal CRUD wired up below in the goals editor.
  const addGoal = useAppStore(s => s.addGoal);
  const updateGoal = useAppStore(s => s.updateGoal);
  const removeGoal = useAppStore(s => s.removeGoal);

  // Loan state: computed from purchases.loanFundedUsd + loanEvents.
  // Falls back to current Aave APR snapshot when an event has no aprSnapshot.
  const loanEvents = useAppStore(s => s.loanEvents);
  const aaveSnapshot = useAppStore(s => s.aaveSnapshot);
  const lltv = useAppStore(s => s.loopLoanInputs.lltv);
  const fallbackApr = aaveSnapshot?.borrowApr ?? new Decimal('0.06');

  const effectivePriceUsd = liveSnapshot?.priceUsd ?? ZERO;
  const effectiveAthUsd = liveSnapshot?.athUsd ?? ZERO;

  const fetchLive = async (): Promise<void> => {
    setLiveStatus('loading');
    try {
      const snap = await provider.fetchBtcUsd();
      setLiveSnapshot(snap);
      setLiveStatus('ok');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setLiveStatus('error', msg);
      // Patch CW: re-throw so useAutoRefresh's retry-on-failure chain
      // sees this as a rejection. Pre-CW the try/catch swallowed the
      // error and resolved successfully, defeating the retry logic from
      // CP - users on mobile PWA cold-starts saw "live error" until they
      // manually refreshed because the hook's .catch() never fired.
      throw e;
    }
  };

  // Patch N: refresh every ETF holding's price in sequence (in parallel via
  // Promise.allSettled would hammer the proxy; sequential keeps it polite).
  // Errors are accumulated; a single failure doesn't abort the rest.
  const fetchEtfsAll = async (): Promise<void> => {
    if (etfHoldings.length === 0) return;
    setEtfPriceStatus('loading');
    let lastErr: string | null = null;
    for (const h of etfHoldings) {
      try {
        const snap = await fetchEtfPrice(h.symbol);
        setEtfLivePrice(h.id, snap);
      } catch (e) {
        lastErr = e instanceof Error ? e.message : String(e);
      }
    }
    setEtfPriceStatus(lastErr ? 'error' : 'ok', lastErr);
  };

  // Unified refresh: BTC + ETFs in parallel. Each updates its own status
  // independently so a failure on one side doesn't poison the other.
  const refreshPrices = async (): Promise<void> => {
    await Promise.allSettled([fetchLive(), fetchEtfsAll()]);
  };

  // Auto-refresh BTC price: on mount, on tab return, every 2 minutes while visible.
  useAutoRefresh(fetchLive, {
    intervalMs: 2 * 60 * 1000,
    minGapMs: 30_000,
  });

  const dash = useMemo(
    () => selectDashboard({
      purchases,
      goals,
      currentBtcPriceUsd: effectivePriceUsd,
      asOf: new Date(),
      paceMethod,
    }),
    [purchases, goals, effectivePriceUsd, paceMethod],
  );

  const loanState = useMemo(
    () => computeLoanState({
      purchases,
      loanEvents,
      asOf: new Date(),
      fallbackApr,
    }),
    [purchases, loanEvents, fallbackApr],
  );

  // Loan position metrics (only meaningful when loan > 0).
  const haveLoan = loanState.currentBalance.greaterThan(0);
  const collateralUsd = dash.stackUsd;
  const loanLtv = haveLoan ? ltvFn(loanState.currentBalance, collateralUsd) : ZERO;
  const loanHf = haveLoan ? hfFn(collateralUsd, lltv, loanState.currentBalance) : ZERO;
  const loanLiqPrice = haveLoan && dash.totalBtc.greaterThan(0) && lltv.greaterThan(0)
    ? loanState.currentBalance.div(dash.totalBtc.times(lltv))
    : ZERO;

  const drawdownFromAth = effectiveAthUsd.isZero()
    ? new Decimal(0)
    : effectivePriceUsd.minus(effectiveAthUsd).div(effectiveAthUsd);

  const havePrice = !effectivePriceUsd.isZero();

  // Patch N: goal editor local state. New-goal form mirrors strings while
  // typing (so partial input doesn't get fed to the store as garbage); the
  // existing-goals inline editors push to the store directly via
  // updateGoal because they're already-validated Decimals.
  const [newGoalLabel, setNewGoalLabel] = useState<string>('');
  const [newGoalTargetStr, setNewGoalTargetStr] = useState<string>('');

  const handleAddGoal = (): void => {
    const target = parseFloat(newGoalTargetStr);
    const label = newGoalLabel.trim();
    if (!label || !isFinite(target) || target <= 0) return;
    addGoal({
      label,
      targetBtc: new Decimal(target),
      // Goal type requires `enabled: boolean`; new goals default to enabled.
      // No UI to toggle this currently; a disable affordance could be added
      // to the editor later if you want to keep dormant goals around without
      // deleting them.
      enabled: true,
    });
    setNewGoalLabel('');
    setNewGoalTargetStr('');
  };

  const refreshDisabled = liveStatus === 'loading' || etfPriceStatus === 'loading';

  return (
    <>
      {/* HERO: two-up layout per spec.
          USD on the left (slightly smaller), BTC on the right (slightly bigger,
          since "stack" framing emphasizes the BTC count over fiat value). */}
      <section className="section">
        <p className="section-eyebrow">Stack value at current price</p>
        <div
          className="row"
          style={{
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 'var(--gap-xl)',
            marginBottom: 'var(--gap-m)',
          }}
        >
          {/* USD: left side */}
          <div>
            <div
              className="mono"
              style={{
                fontSize: 'clamp(2.4rem, 5.5vw, 3.4rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
                fontWeight: 400,
                color: havePrice ? 'var(--text)' : 'var(--text-faint)',
              }}
            >
              {havePrice ? <>${formatUsd(dash.stackUsd, { decimals: 2 })}</> : '-'}
            </div>
            {havePrice ? (
              <div className="mono" style={{ fontSize: 'var(--fs-small)', marginTop: 6, color: 'var(--text-soft)' }}>
                <Delta usd={dash.gainUsd} /> <span className="faint">vs cost</span>{' '}
                <Pct value={dash.gainPctCost} />
              </div>
            ) : null}
          </div>

          {/* BTC: right side, slightly bigger; sats below */}
          <div>
            <div
              className="mono"
              style={{
                fontSize: 'clamp(2.6rem, 6vw, 3.8rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
                fontWeight: 400,
                color: 'var(--accent)',
              }}
            >
              {formatBtc(dash.totalBtc)} <span style={{ fontSize: '0.55em', letterSpacing: '0.04em', color: 'var(--text-soft)', fontWeight: 500 }}>BTC</span>
            </div>
            <div className="mono" style={{ fontSize: 'var(--fs-small)', marginTop: 6, color: 'var(--text-soft)' }}>
              {formatSats(dash.totalSats)} sats
            </div>
          </div>
        </div>

        {/* Tiny loan readout: only shown if the user has an active loop loan. */}
        {haveLoan ? (
          <div
            style={{
              marginTop: 'var(--gap-s)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--gap-m)',
              fontSize: 'var(--fs-small)',
              fontFamily: 'var(--mono)',
              color: 'var(--text-soft)',
              borderLeft: '2px solid var(--accent)',
              paddingLeft: 'var(--gap-s)',
            }}
            title="Loop loan position. Liq price assumes your full BTC stack is the collateral."
          >
            <span>
              <span className="faint">loan</span> ${formatUsd(loanState.currentBalance, { decimals: 0 })}
            </span>
            <span>
              <span className="faint">liq</span>{' '}
              <span style={{ color: havePrice && effectivePriceUsd.lessThan(loanLiqPrice.times('1.15')) ? 'var(--neg)' : 'var(--text-soft)' }}>
                ${formatUsd(loanLiqPrice, { decimals: 0 })}
              </span>
            </span>
            <span>
              <span className="faint">HF</span>{' '}
              <span style={{ color: loanHf.lessThan('1.2') ? 'var(--neg)' : loanHf.lessThan('1.5') ? 'var(--warn)' : 'var(--pos)' }}>
                {loanHf.toFixed(2)}
              </span>
            </span>
            <span className="faint" style={{ fontSize: 'var(--fs-micro)' }}>
              · LTV {formatPct(loanLtv)} · APR {formatPct(loanState.currentApr)} <span title="Aave's variable rate; balance accrues at the APR snapshotted at the most recent loan event.">(variable)</span>
            </span>
          </div>
        ) : null}
      </section>

      {/* PRICE HISTORY CHART */}
      <Suspense fallback={<ChartFallback />}>
        <BtcPriceChart />
      </Suspense>

      {/* STATS */}
      <section className="section">
        <p className="section-eyebrow">Position</p>
        <div className="stat-grid">
          <StatBox
            label="Cost basis avg"
            value={dash.totalBtc.isZero() ? '-' : <>${formatUsd(dash.totalInvested.div(dash.totalBtc), { decimals: 2 })}</>}
            sub={<>per BTC</>}
          />
          <StatBox
            label="Total invested"
            value={<>${formatUsd(dash.totalInvested, { decimals: 0 })}</>}
            sub={<>{purchases.length} purchase{purchases.length === 1 ? '' : 's'}</>}
          />
          <StatBox
            label="Current BTC price"
            value={havePrice ? <>${formatUsd(effectivePriceUsd, { decimals: 0 })}</> : '-'}
            sub={
              liveSnapshot ? (
                <>live · {liveSnapshot.fetchedAt.toLocaleTimeString()}</>
              ) : liveStatus === 'loading' ? (
                <>fetching...</>
              ) : (
                <>not yet fetched</>
              )
            }
          />
          <StatBox
            label="All-time high"
            value={havePrice ? <>${formatUsd(effectiveAthUsd, { decimals: 0 })}</> : '-'}
            sub={
              havePrice ? (
                <>
                  from ATH{' '}
                  <span className={drawdownFromAth.lessThan(0) ? 'mono delta neg' : 'mono delta pos'}>
                    {drawdownFromAth.greaterThan(0) ? '+' : ''}
                    {formatPct(drawdownFromAth)}
                  </span>
                </>
              ) : null
            }
          />
        </div>
      </section>

      {/* GOALS */}
      <section className="section">
        <p className="section-eyebrow">{isFIRETenant() ? 'FIRE goals' : 'Goals'}</p>
        <div className="row between" style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--gap-m)' }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            {paceMethod === 'allTimePace' ? 'Progress at all-time pace' : 'Progress at recent pace (90d)'}
          </h2>
          <div className="row" style={{ gap: 'var(--gap-xs)' }}>
            <button
              className={paceMethod === 'allTimePace' ? 'primary' : ''}
              onClick={() => setPaceMethod('allTimePace')}
              style={{ fontSize: 'var(--fs-micro)', padding: '4px 10px' }}
            >
              All-time
            </button>
            <button
              className={paceMethod === 'recentPace' ? 'primary' : ''}
              onClick={() => setPaceMethod('recentPace')}
              style={{ fontSize: 'var(--fs-micro)', padding: '4px 10px' }}
            >
              Recent 90d
            </button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--gap-l)' }}>
          {dash.goals.length === 0 ? (
            <p className="muted">{isFIRETenant() ? 'No FIRE milestones. Add a BTC target below to track your path to FI.' : 'No goals set yet.'}</p>
          ) : (
            <div>
              {dash.goals.map(g => {
                const pct = g.progress.times(100);
                const complete = g.progress.greaterThanOrEqualTo(1);
                return (
                  <div className="goal-row" key={g.goal.id}>
                    <div className="name">{g.goal.label}</div>
                    <div className="meter">
                      <div
                        className={`fill${complete ? ' complete' : ''}`}
                        style={{ width: `${Math.min(100, Math.max(0, pct.toNumber()))}%` }}
                      />
                    </div>
                    <div className="pct">
                      {formatPct(g.progress)}
                      {!complete && g.eta.date ? (
                        <>
                          <br />
                          <span className="faint">ETA {formatDate(g.eta.date)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Patch N: goals editor. Lives at the bottom of the goals card per
            spec, separated by a thin rule so the progress display above stays
            visually clean. Each existing goal is inline-editable (label and
            target BTC) with a delete button; the row at the bottom is the
            "add new" form. Strings for the new-goal inputs prevent invalid
            partials from polluting the store; the existing rows use
            DecimalInput which handles the controlled-input editing bug. */}
        <div style={{ marginTop: 'var(--gap-l)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--gap-m)' }}>
          <p className="faint" style={{ fontSize: 'var(--fs-micro)', marginBottom: 'var(--gap-s)', letterSpacing: '0.04em' }}>
            {isFIRETenant() ? 'EDIT MILESTONES' : 'EDIT GOALS'}
          </p>
          {goals.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--gap-m)' }}>
              {goals.map(g => (
                <div key={g.id} className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={g.label}
                    onChange={(e) => updateGoal(g.id, { label: e.target.value })}
                    style={{ flex: '1 1 140px', minWidth: 100, fontSize: 'var(--fs-small)' }}
                  />
                  <DecimalInput
                    value={g.targetBtc}
                    step="0.1"
                    decimals={4}
                    onChange={(d) => updateGoal(g.id, { targetBtc: d })}
                    style={{ width: 100, fontSize: 'var(--fs-small)' }}
                  />
                  <span className="faint mono" style={{ fontSize: 'var(--fs-micro)' }}>BTC</span>
                  <button
                    onClick={() => removeGoal(g.id)}
                    className="ghost"
                    style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)' }}
                    title={isFIRETenant() ? 'Delete this milestone' : 'Delete this goal'}
                  >
                    {'\u2715'}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder={isFIRETenant() ? 'Add milestone' : 'New goal label (e.g. Whole coiner)'}
              value={newGoalLabel}
              onChange={(e) => setNewGoalLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddGoal(); }}
              style={{ flex: '1 1 140px', minWidth: 100, fontSize: 'var(--fs-small)' }}
            />
            <input
              type="number"
              step="0.01"
              placeholder="0"
              value={newGoalTargetStr}
              onChange={(e) => setNewGoalTargetStr(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddGoal(); }}
              style={{ width: 100, fontSize: 'var(--fs-small)' }}
            />
            <span className="faint mono" style={{ fontSize: 'var(--fs-micro)' }}>BTC</span>
            <button
              onClick={handleAddGoal}
              disabled={!newGoalLabel.trim() || !parseFloat(newGoalTargetStr) || parseFloat(newGoalTargetStr) <= 0}
              style={{ padding: '4px 14px', fontSize: 'var(--fs-micro)' }}
            >
              {isFIRETenant() ? 'Add milestone' : 'Add goal'}
            </button>
          </div>
        </div>
      </section>

      {/* RETIREMENT PLAN HOLDINGS (smaller) */}
      <EtfHoldings />

      {/* NET WORTH (with pie chart, Patch N) */}
      <NetWorth />

      {/* LIVE STATUS: Patch N moved this to be the LAST section on the page.
          The unified Refresh button now triggers BTC + ETF refreshes in
          parallel via refreshPrices() which uses Promise.allSettled so a
          failure on one source doesn't poison the other. Button is disabled
          while either fetch is in flight to prevent button-mashing. */}
      <section className="section">
        <p className="section-eyebrow">Live data</p>
        <div className="row" style={{ gap: 'var(--gap-m)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <button
            onClick={() => void refreshPrices()}
            disabled={refreshDisabled}
            title="Refresh BTC live price and all Bitcoin ETF positions"
          >
            {refreshDisabled ? 'Fetching...' : 'Refresh prices'}
          </button>
          {liveSnapshot ? (
            <span className="mono faint" style={{ fontSize: 'var(--fs-small)' }}>
              BTC: CoinGecko · last fetched {liveSnapshot.fetchedAt.toLocaleString()}
            </span>
          ) : null}
          {etfHoldings.length > 0 ? (
            <span className="mono faint" style={{ fontSize: 'var(--fs-small)' }}>
              · ETFs: {etfHoldings.length} symbol{etfHoldings.length === 1 ? '' : 's'} tracked
            </span>
          ) : null}
        </div>
        {liveError ? (() => {
          /* Tiered error severity: a transient CoinGecko rate-limit on
           * one out of a hundred polls shouldn't look like the system is
           * broken when we still have BTC's price from 90 seconds ago.
           *
           *   no snapshot    => full red alarm (genuinely broken / first load)
           *   snapshot < 10m => small dim line beneath the price (most cases)
           *   snapshot >= 10m => amber notice (worth flagging, data may be wrong)
           */
          const ageMs = liveSnapshot
            ? Date.now() - liveSnapshot.fetchedAt.getTime()
            : Infinity;
          const STALE_MS = 10 * 60 * 1000;
          if (!liveSnapshot) {
            return (
              <div className="notice" style={{ marginTop: 'var(--gap-m)', borderLeftColor: 'var(--neg)' }}>
                <strong>Live fetch failed.</strong> {liveError} No previous values available, try Refresh.
              </div>
            );
          }
          if (ageMs >= STALE_MS) {
            return (
              <div className="notice" style={{ marginTop: 'var(--gap-m)', borderLeftColor: 'var(--warn)' }}>
                <strong>Live data is stale.</strong> Last successful fetch {liveSnapshot.fetchedAt.toLocaleString()}. Latest retry: {liveError}
              </div>
            );
          }
          return (
            <div className="faint mono" style={{ marginTop: 'var(--gap-s)', fontSize: 'var(--fs-micro)' }}>
              · last poll missed ({liveError}). showing values from {liveSnapshot.fetchedAt.toLocaleTimeString()}
            </div>
          );
        })() : null}
      </section>
    </>
  );
}
